import { Info } from '@phosphor-icons/react';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { MoonLoader } from 'react-spinners';

interface TextModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: string, nickname?: string) => void;
  title: string;
  value?: string;
  nickname?: string;
  showNickname?: boolean;
  loader?: JSX.Element;
  loading?: boolean;
  submitBtnText?: string;
  cancelBtnText?: string;
  information?: {
    title: string;
    description: string;
  };
}

const TextModalInput: React.FC<TextModalProps> = (props: TextModalProps) => {
  const {
    isOpen,
    onClose,
    onSubmit,
    value,
    nickname,
    showNickname = false,
    title,
    loading,
    loader,
    submitBtnText,
    cancelBtnText,
    information,
  } = props;
  const [inputValue, setInputValue] = useState<string>(value || '');
  const [nicknameValue, setNicknameValue] = useState<string>(nickname || '');

  useEffect(() => {
    if (value && value !== inputValue) {
      setInputValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (nickname !== undefined && nickname !== nicknameValue) {
      setNicknameValue(nickname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nickname]);

  const handleSubmit = () => {
    onSubmit(inputValue, showNickname ? nicknameValue : undefined);
  };

  const handleClose = () => {
    onClose();
    if (value) {
      setInputValue(value);
    }
    if (nickname) {
      setNicknameValue(nickname);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(event.target.value);
  };

  const handleNicknameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNicknameValue(event.target.value);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      id="text-modal-input"
      data-testid="test-text-modal-input"
      className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-75"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="flex h-[70vh] max-h-[80vh] w-[60vw] max-w-4xl flex-col rounded-lg bg-white p-6"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
      >
        <h2 className="mb-4 text-center text-xl font-semibold text-neutral-700">
          {title}
        </h2>
        {information && (
          <div className="border-l-3 mb-4 flex items-start gap-2 rounded-md border-blue-500 bg-blue-50 p-3">
            <Info className="mt-0.5 flex-shrink-0 text-blue-600" size={16} />
            <div className="text-sm text-blue-800">
              <p className="font-medium">{information.title}</p>
              <p className="mt-1">{information.description}</p>
            </div>
          </div>
        )}

        {showNickname && (
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Nickname (Optional)
            </label>
            <input
              type="text"
              value={nicknameValue}
              onChange={handleNicknameChange}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
              placeholder="Enter nickname"
            />
          </div>
        )}

        <textarea
          data-testid="test-text-area-modal-input"
          className="mb-2 w-full flex-grow resize-none rounded-md border border-primary-100 p-2 text-xs outline-primary-500 sm:text-sm"
          placeholder="Type your input here..."
          value={inputValue}
          onChange={handleChange}
        />

        {/* Submit Button */}
        <div className="flex w-full items-center justify-end gap-3">
          <button
            className="pr-15 mb-2 mt-5 flex w-20 items-center justify-center space-x-2 whitespace-nowrap rounded-md bg-brandError-700 px-2 py-2 text-xs text-white"
            onClick={handleClose}
          >
            {cancelBtnText || 'Cancel'}
          </button>
          <button
            data-testid="test-text-modal-submit-btn"
            className="mb-2 mt-5 flex w-20 flex-row items-center justify-center space-x-2 whitespace-nowrap rounded-md bg-primary-700 px-2 py-2 text-xs text-white"
            onClick={handleSubmit}
          >
            {loading
              ? loader || <MoonLoader size={12} color="#FFFFFF" />
              : submitBtnText || 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TextModalInput;
